import { List, Record } from 'immutable'
import { Address, IAddress } from './Address'
import {
  AssociatedDevelopmentClient,
  IAssociatedDevelopmentClient,
} from './AssociatedDevelopmentClient'
import {
  CriticalDateContact,
  ICriticalDateContact,
} from './CriticalDateContact'
import { DueDiligenceItem, IDueDiligenceItem } from './DueDiligenceItem'

export interface IDevelopmentCriticalDatesDocument {
  readonly address: IAddress
  readonly buyerBrokers: List<ICriticalDateContact>
  readonly buyers: List<ICriticalDateContact>
  readonly client: IAssociatedDevelopmentClient
  readonly closingDate?: number
  readonly criticalDates: List<IDueDiligenceItem>
  readonly earnestMoney?: IDueDiligenceItem
  readonly effectiveDate?: number
  readonly name: string
  readonly price: number
  readonly sellerBrokers: List<ICriticalDateContact>
  readonly sellers: List<ICriticalDateContact>
  readonly siteId: string
  readonly title: string
  readonly titleCompanies: List<ICriticalDateContact>
  readonly titleCompanyContacts: List<ICriticalDateContact>
}

const DevelopmentCriticalDatesDocumentRecord = Record({
  address: new Address(),
  buyerBrokers: List<CriticalDateContact>(),
  buyers: List<CriticalDateContact>(),
  client: new AssociatedDevelopmentClient(),
  closingDate: undefined,
  criticalDates: List<DueDiligenceItem>(),
  earnestMoney: undefined,
  effectiveDate: undefined,
  name: '',
  price: 0,
  sellerBrokers: List<CriticalDateContact>(),
  sellers: List<CriticalDateContact>(),
  siteId: '',
  title: '',
  titleCompanies: List<CriticalDateContact>(),
  titleCompanyContacts: List<CriticalDateContact>(),
})

export class DevelopmentCriticalDatesDocument
  extends DevelopmentCriticalDatesDocumentRecord
  implements IDevelopmentCriticalDatesDocument
{
  public static fromJS(
    initialState?: Partial<IDevelopmentCriticalDatesDocument>,
  ) {
    if (!initialState) {
      return new DevelopmentCriticalDatesDocument()
    }

    const {
      address,
      buyerBrokers,
      buyers,
      client,
      criticalDates,
      earnestMoney,
      sellerBrokers,
      sellers,
      titleCompanies,
      titleCompanyContacts,
      ...rest
    } = initialState

    return new DevelopmentCriticalDatesDocument({
      address: Address.fromJS(address),
      buyerBrokers: buyerBrokers
        ? List(buyerBrokers.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      buyers: buyers
        ? List(buyers.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      client: AssociatedDevelopmentClient.fromJS(client),
      criticalDates: criticalDates
        ? List(criticalDates.map((x) => DueDiligenceItem.fromJS(x)))
        : List<DueDiligenceItem>(),
      earnestMoney: earnestMoney
        ? DueDiligenceItem.fromJS(earnestMoney)
        : undefined,
      sellerBrokers: sellerBrokers
        ? List(sellerBrokers.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      sellers: sellers
        ? List(sellers.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      titleCompanies: titleCompanies
        ? List(titleCompanies.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      titleCompanyContacts: titleCompanyContacts
        ? List(titleCompanyContacts.map((x) => CriticalDateContact.fromJS(x)))
        : List<CriticalDateContact>(),
      ...rest,
    })
  }

  public readonly address: Address
  public readonly buyerBrokers: List<CriticalDateContact>
  public readonly buyers: List<CriticalDateContact>
  public readonly client: AssociatedDevelopmentClient
  public readonly closingDate?: number
  public readonly criticalDates: List<DueDiligenceItem>
  public readonly earnestMoney?: DueDiligenceItem
  public readonly effectiveDate?: number
  public readonly name: string
  public readonly price: number
  public readonly sellerBrokers: List<CriticalDateContact>
  public readonly sellers: List<CriticalDateContact>
  public readonly siteId: string
  public readonly title: string
  public readonly titleCompanies: List<CriticalDateContact>
  public readonly titleCompanyContacts: List<CriticalDateContact>

  constructor(props?: Partial<DevelopmentCriticalDatesDocument>) {
    props ? super(props) : super()
  }
}
